import {
  ColumnMenuVisibleChangedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  DragStoppedEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';

interface GridSettings {
  columnState: ColumnState[];
  groupState: {
    groupId: string;
    open: boolean;
  }[];
  filterState: Record<string, any>;
}

export class GridPersist {
  private gridReady = false;
  private gridId = '';
  private pendingFilterState: Record<string, any> | null = null;

  constructor(gridId: string) {
    this.gridId = gridId;
  }

  private saveGridSettings(gridApi: GridApi, force = false) {
    // An explicit reset must persist even when no row has rendered yet (e.g. a restored filter is hiding every row);
    // otherwise the stale filter would be restored on the next load.
    if (!force && this.gridReady !== true) {
      return;
    }
    const state = {
      columnState: gridApi.getColumnState(),
      groupState: gridApi.getColumnGroupState(),
      filterState: gridApi.getFilterModel(),
    } as GridSettings;
    const json = JSON.stringify(state);
    localStorage.setItem(`ag-persist-grid-settings-${this.gridId}`, json);
  }

  private getGridSettings() {
    const gridSettings = localStorage.getItem(`ag-persist-grid-settings-${this.gridId}`);
    if (gridSettings) {
      return JSON.parse(gridSettings) as GridSettings;
    }
    return null;
  }

  private disableGridAnimations() {
    const style = document.createElement('style');
    style.innerHTML = '.ag-header-cell, .ag-cell-value { transition: none !important; }';
    document.getElementsByTagName('head')[0].appendChild(style);
    return style;
  }

  private enableGridAnimations(styleObject: HTMLStyleElement) {
    setTimeout(() => {
      document.getElementsByTagName('head')[0].removeChild(styleObject);
    }, 500);
  }

  public applyPersistedFilters(gridApi: GridApi) {
    if (this.pendingFilterState) {
      gridApi.setFilterModel(this.pendingFilterState);
      this.pendingFilterState = null;
    }
  }

  public setup(gridOptions: GridOptions, deferFilterRestore = false) {
    const animationResponse = this.disableGridAnimations();
    return {
      ...gridOptions,
      onDragStopped: (params: DragStoppedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onDragStopped) gridOptions.onDragStopped(params);
      },
      onFilterChanged: (params: FilterChangedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onFilterChanged) gridOptions.onFilterChanged(params);
      },
      onSortChanged: (params: SortChangedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onSortChanged) {
          gridOptions.onSortChanged(params);
        }
      },
      onColumnPinned: (params: ColumnPinnedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onColumnPinned) {
          gridOptions.onColumnPinned(params);
        }
      },
      onColumnMenuVisibleChanged: (params: ColumnMenuVisibleChangedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onColumnMenuVisibleChanged) {
          gridOptions.onColumnMenuVisibleChanged(params);
        }
      },
      onColumnVisible: (params: ColumnVisibleEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onColumnVisible) {
          gridOptions.onColumnVisible(params);
        }
      },
      onColumnResized: (params: ColumnResizedEvent) => {
        this.saveGridSettings(params.api);
        if (gridOptions.onColumnResized) {
          gridOptions.onColumnResized(params);
        }
      },
      onFirstDataRendered: (params: FirstDataRenderedEvent) => {
        this.gridReady = true;

        if (gridOptions.onFirstDataRendered) {
          return gridOptions.onFirstDataRendered(params);
        }
      },
      onGridReady: (params: GridReadyEvent) => {
        const columnState = this.getGridSettings();
        if (columnState) {
          params.api.applyColumnState({ state: columnState.columnState, applyOrder: true });
          params.api.setColumnGroupState(columnState.groupState);
          if (deferFilterRestore) {
            this.pendingFilterState = columnState.filterState;
          } else {
            params.api.setFilterModel(columnState.filterState);
          }
          this.enableGridAnimations(animationResponse);
        }

        if (gridOptions.onGridReady) {
          gridOptions.onGridReady(params);
        }
      },
    } as GridOptions;
  }

  public resetAndSaveGrid(gridApi: GridApi) {
    gridApi.resetColumnState();
    gridApi.setFilterModel(null);
    this.saveGridSettings(gridApi, true);
  }
}
